import { createApi } from "@reduxjs/toolkit/query/react";
import { components, operations } from "../../types/api";
import { baseQueryWithReauth } from "../../utils/baseQuery";

// Response types
type ApiResponseIOMarginPeriodArray =
  components["schemas"]["ApiResponse_IOMarginPeriod-Array_"];

type ApiResponseIOMarginPeriod =
  components["schemas"]["ApiResponse_IOMarginPeriod_"];

type ApiResponseDbMarginPeriodArray =
  components["schemas"]["ApiResponse_InsertionOrderMarginPeriod-Array_"];

type ApiResponseBoolean = components["schemas"]["ApiResponse_boolean_"];

// Request body types from operations
// POST /io-margins/{id}/margins
export type CreateIOMarginPeriodRequest =
  operations["AddMarginPeriod"]["requestBody"]["content"]["application/json"];

// PUT /io-margins/margin-period/{id}
export type UpdateIOMarginPeriodRequest =
  operations["UpdateMarginPeriod"]["requestBody"]["content"]["application/json"];

export const insertionOrderMarginApi = createApi({
  reducerPath: "insertionOrderMarginApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["InsertionOrderMargins"],
  endpoints: (builder) => ({
    // Get consolidated margin periods for an insertion order
    getIOMarginsByInsertionOrderId: builder.query<
      ApiResponseIOMarginPeriodArray,
      string
    >({
      query: (id) => `/io-margins/${id}/margins`,
      providesTags: (result, error, id) => [
        { type: "InsertionOrderMargins" as const, id: `io-${id}` },
        "InsertionOrderMargins",
      ],
    }),

    // Create a new IO margin period
    addIOMarginPeriod: builder.mutation<
      ApiResponseIOMarginPeriod,
      { id: string; data: CreateIOMarginPeriodRequest }
    >({
      query: ({ id, data }) => ({
        url: `/io-margins/${id}/margins`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "InsertionOrderMargins" as const, id: `io-${id}` },
        "InsertionOrderMargins",
      ],
    }),

    // Update an IO margin period by ID
    updateIOMarginPeriod: builder.mutation<
      ApiResponseIOMarginPeriod,
      { id: string; data: UpdateIOMarginPeriodRequest }
    >({
      query: ({ id, data }) => ({
        url: `/io-margins/margin-period/${id}`,
        method: "PUT",
        body: data,
      }),
      // We don't know the IO id from this response shape; invalidate the list globally
      invalidatesTags: ["InsertionOrderMargins"],
    }),

    // Delete an IO margin period by ID
    deleteIOMarginPeriod: builder.mutation<ApiResponseBoolean, string>({
      query: (id) => ({
        url: `/io-margins/margin-period/${id}`,
        method: "DELETE",
      }),
      // We don't know the IO id; invalidate the list globally
      invalidatesTags: ["InsertionOrderMargins"],
    }),

    // Get raw IO margin period entries from the database for an insertion order
    getRawIOMarginPeriods: builder.query<
      ApiResponseDbMarginPeriodArray,
      string
    >({
      query: (id) => `/io-margins/${id}/raw-margins`,
      providesTags: (result, error, id) => [
        { type: "InsertionOrderMargins" as const, id: `io-${id}` },
        "InsertionOrderMargins",
      ],
    }),
  }),
});

export const {
  useGetIOMarginsByInsertionOrderIdQuery,
  useAddIOMarginPeriodMutation,
  useUpdateIOMarginPeriodMutation,
  useDeleteIOMarginPeriodMutation,
  useGetRawIOMarginPeriodsQuery,
} = insertionOrderMarginApi;
